import { ProvisioningError } from "./link";
import { findIdentity, IDENTITY_COMMAND } from "./identity";
import type { DeviceIdentity, DeviceLink } from "./link";
import type { FirmwareManifest } from "./manifest";

/**
 * The real cable: Web Serial, ESP Web Tools and Improv.
 *
 * Three libraries, one port. ESP Web Tools' `flash()` opens the port itself,
 * writes the firmware through esptool-js and closes it again; the Improv
 * client from improv-wifi-serial-sdk expects an open port and speaks the
 * open standard the device answers for its network; the identity is one
 * plain-text line read with the port's own streams. Each is loaded on first
 * use, so the rest of the back office does not carry a flasher around.
 *
 * Only Chromium ships Web Serial, which is why the page says "Chrome or Edge
 * on a laptop" rather than trying anyway.
 */

const BAUD_RATE = 115200;

/** How long a device gets to boot and answer Improv after a flash. */
const IMPROV_DETECT_MS = 30_000;
/** How long it gets to take the credentials and reach the network. */
const IMPROV_PROVISION_MS = 45_000;
/**
 * How long it gets to answer the identity command.
 *
 * Long enough to cover a device that resets when the port is reopened after
 * the Wi-Fi handover and has to boot again before it can answer.
 */
const IDENTITY_MS = 30_000;
/** How often the question is repeated while waiting. */
const IDENTITY_ASK_EVERY_MS = 2_000;
/** How long it gets to say which networks it can hear, after a failed join. */
const SCAN_MS = 20_000;

export function isWebSerialSupported(): boolean {
  return typeof navigator !== "undefined" && "serial" in navigator;
}

/** A DOMException name, if that is what was thrown. */
function nameOf(error: unknown): string | undefined {
  const named = error as { name?: unknown };
  return typeof named?.name === "string" ? named.name : undefined;
}

/**
 * Asks the person for a port.
 *
 * Must run inside the click that started the flow: the browser only shows its
 * picker on a user gesture. Returns nothing when the picker is dismissed —
 * that is a decision, not a failure, and the page shows no error for it.
 */
export async function requestDeviceLink(
  manifest: FirmwareManifest,
  manifestUrl: string
): Promise<DeviceLink | undefined> {
  let port: SerialPort;
  try {
    port = await navigator.serial.requestPort();
  } catch (error) {
    if (nameOf(error) === "NotFoundError") return undefined;
    throw new ProvisioningError("flash", "devices.add.error.port", { cause: error });
  }
  return new WebSerialLink(port, manifest, manifestUrl);
}

/** What ESP Web Tools reports while it works; typed loosely so its enums stay its own. */
interface FlashEvent {
  state: string;
  message: string;
  details?: unknown;
}

/** The sentence for each way ESP Web Tools can give up. */
const FLASH_ERROR_KEYS: Record<string, string> = {
  failed_initialize: "devices.add.error.noResponse",
  not_supported: "devices.add.error.wrongBoard",
  fetch_manifest_failed: "devices.add.error.firmwareMissing",
  failed_firmware_download: "devices.add.error.firmwareMissing",
  write_failed: "devices.add.error.flash",
};

/** The sentence for each way the device can refuse its network. */
const IMPROV_ERROR_KEYS: Record<string, string> = {
  UNABLE_TO_CONNECT: "devices.add.error.networkUnreachable",
  TIMEOUT: "devices.add.error.network",
};

/** A logger for the Improv client that keeps its chatter out of the console. */
const QUIET = {
  log: () => undefined,
  error: (message: string, ...rest: unknown[]) => console.error(message, ...rest),
  debug: () => undefined,
};

class WebSerialLink implements DeviceLink {
  private closed = false;

  constructor(
    private readonly port: SerialPort,
    private readonly manifest: FirmwareManifest,
    private readonly manifestUrl: string
  ) {}

  async flash(onProgress: (percent: number) => void): Promise<void> {
    const { flash } = await import("esp-web-tools/dist/flash.js");

    /*
      `flash()` reports rather than throws: every failure arrives as an event
      with the state "error", and the promise resolves afterwards regardless.
      So the outcome is read off the events, and the promise settling without
      a "finished" is itself a failure.
    */
    let outcome: "finished" | ProvisioningError | undefined;
    const onEvent = (event: FlashEvent) => {
      if (event.state === "writing") {
        const details = event.details as { percentage?: unknown } | undefined;
        if (typeof details?.percentage === "number") onProgress(details.percentage);
      } else if (event.state === "finished") {
        outcome = "finished";
      } else if (event.state === "error") {
        const details = event.details as { error?: unknown; details?: unknown } | undefined;
        const code = typeof details?.error === "string" ? details.error : "";
        outcome = new ProvisioningError("flash", FLASH_ERROR_KEYS[code] ?? "devices.add.error.flash", {
          cause: details?.details ?? event.message,
        });
      }
    };

    try {
      // Nothing is ever erased: the storage partition holds the device's seed,
      // and a re-flash that wiped it would be a new device (K3).
      await flash(
        onEvent as Parameters<typeof flash>[0],
        this.port,
        this.manifestUrl,
        this.manifest as Parameters<typeof flash>[3],
        false
      );
    } catch (error) {
      throw new ProvisioningError("flash", "devices.add.error.flash", { cause: error });
    }

    if (outcome === undefined) throw new ProvisioningError("flash", "devices.add.error.flash");
    if (outcome !== "finished") throw outcome;
  }

  async sendNetwork(ssid: string, psk: string): Promise<void> {
    const { ImprovSerial } = await import("improv-wifi-serial-sdk/dist/serial.js");
    await this.open();

    const client = new ImprovSerial(this.port, QUIET);
    try {
      // The device is booting — on its first boot it is also making its
      // identity — and the client re-asks every second until it answers.
      await client.initialize(IMPROV_DETECT_MS);
    } catch (error) {
      throw new ProvisioningError("network", "devices.add.error.noImprov", { cause: error });
    }

    try {
      await client.provision(ssid, psk, IMPROV_PROVISION_MS);
    } catch (error) {
      const code = typeof error === "string" ? error : "";

      /*
       * A device that could not join is asked what it CAN see.
       *
       * "The device could not reach the kit's Wi-Fi" is one sentence for two
       * completely different problems — a router that is not broadcasting at
       * all, and a router that is there and refused us — and the person
       * holding the device has no way to tell which. The firmware has
       * answered a scan since it was written and nothing ever asked it. It is
       * asked here, while the client is still open, and the answer decides
       * which sentence they get (2026-09-12, on the first real device).
       */
      if (code === "UNABLE_TO_CONNECT") {
        const seen = await this.networksInRange(client);
        if (seen !== undefined) {
          const found = seen.some((name) => name === ssid);
          const key = found
            ? "devices.add.error.networkRefused"
            : seen.length === 0
              ? "devices.add.error.networkNotSeenNothing"
              : "devices.add.error.networkNotSeen";

          await client.close();
          throw new ProvisioningError("network", key, {
            cause: error,
            params: { ssid, seen: seen.join(", ") },
          });
        }
      }

      throw new ProvisioningError("network", IMPROV_ERROR_KEYS[code] ?? "devices.add.error.network", {
        cause: error,
      });
    } finally {
      // Lets go of the reader; the port stays open for the identity read.
      await client.close();
    }
  }

  /**
   * The names of the networks the device can hear, or nothing.
   *
   * Best effort by design: this runs when something has already gone wrong,
   * and a scan that also fails must not replace the real failure with its own.
   */
  private async networksInRange(client: {
    scan: (timeout?: number) => Promise<Array<{ name: string }>>;
  }): Promise<string[] | undefined> {
    try {
      const found = await client.scan(SCAN_MS);
      return [...new Set(found.map((network) => network.name).filter(Boolean))].sort();
    } catch {
      return undefined;
    }
  }

  async readIdentity(): Promise<DeviceIdentity> {
    await this.open();
    if (!this.port.readable || !this.port.writable) {
      throw new ProvisioningError("identity", "devices.add.error.identity");
    }

    const reader = this.port.readable.getReader();
    const decoder = new TextDecoder();
    let buffer = "";

    /*
     * Asked again and again, not once.
     *
     * The question was put a single time and then ten seconds were spent
     * waiting for an answer — so a device that happened to be busy, or that
     * reset when the port was reopened after the Wi-Fi handover (opening a
     * serial port asserts DTR, and on an ESP32 that is a reset line), missed
     * the only time it was ever asked and the whole step failed. It answers
     * this whenever it is asked and the answer is the same every time, so
     * asking again costs nothing (2026-09-12, on the first real device).
     */
    const ask = () => {
      if (!this.port.writable) return;
      const writer = this.port.writable.getWriter();
      void writer
        .write(new TextEncoder().encode(IDENTITY_COMMAND))
        .catch(() => undefined)
        .finally(() => writer.releaseLock());
    };

    ask();
    const again = window.setInterval(ask, IDENTITY_ASK_EVERY_MS);
    const deadline = window.setTimeout(() => void reader.cancel(), IDENTITY_MS);
    try {
      for (;;) {
        const { value, done } = await reader.read();
        if (done) break;
        buffer += decoder.decode(value, { stream: true });
        const identity = findIdentity(buffer);
        if (identity) return identity;
      }
    } catch (error) {
      throw new ProvisioningError("identity", "devices.add.error.identity", { cause: error });
    } finally {
      window.clearInterval(again);
      window.clearTimeout(deadline);
      reader.releaseLock();
    }
    throw new ProvisioningError("identity", "devices.add.error.identity", { cause: buffer });
  }

  async close(): Promise<void> {
    if (this.closed) return;
    this.closed = true;
    try {
      if (this.port.readable || this.port.writable) await this.port.close();
    } catch {
      // Already closed, or unplugged. Either way there is nothing to hold.
    }
  }

  /** Opens the port at the device's rate, unless it already is. */
  private async open(): Promise<void> {
    if (this.port.readable && this.port.writable) return;
    try {
      // The same buffer ESP Web Tools uses after a flash: the boot log is
      // long, and a small buffer drops the line we are waiting for.
      await this.port.open({ baudRate: BAUD_RATE, bufferSize: 8192 });
    } catch (error) {
      throw new ProvisioningError("network", "devices.add.error.portBusy", { cause: error });
    }
  }
}
